import type { UnifiedChatMessage } from '@/mocks/adminLiveChat';
import {
  sourceColors,
  sourceIcons,
  typeIcons,
  typeColors,
  statusColors,
  priorityColors,
  priorityIcons,
} from '@/mocks/adminLiveChat';

interface Props {
  message: UnifiedChatMessage;
  isSelected: boolean;
  isHostView: boolean;
  onSelect: (msg: UnifiedChatMessage) => void;
  onToggleFavorite: (msg: UnifiedChatMessage) => void;
  onTogglePin: (msg: UnifiedChatMessage) => void;
  onMarkAir: (msg: UnifiedChatMessage) => void;
  onArchive: (msg: UnifiedChatMessage) => void;
}

export default function UnifiedMessageCard({
  message,
  isSelected,
  isHostView,
  onSelect,
  onToggleFavorite,
  onTogglePin,
  onMarkAir,
  onArchive,
}: Props) {
  const sourceStyle = sourceColors[message.source];
  const typeStyle = typeColors[message.type];
  const statusStyle = statusColors[message.status];
  const isUnread = message.status === 'nuevo' || message.status === 'pendiente';
  const isPinned = message.status === 'fijado';
  const isFavorite = message.status === 'favorito';

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return d.toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('es-CR', { day: 'numeric', month: 'short' });
  };

  return (
    <button
      onClick={() => onSelect(message)}
      className={`w-full text-left py-3 cursor-pointer transition-all border-b border-background-100 hover:bg-background-50 group ${
        isSelected
          ? 'bg-secondary-50 border-l-[3px] border-l-secondary-500'
          : isUnread
            ? 'bg-background-50'
            : 'border-l-[3px] border-l-transparent'
      } ${isHostView ? 'px-3' : 'px-4'}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="relative shrink-0">
          <div className="w-9 h-9 rounded-full bg-background-200 overflow-hidden">
            <img
              src={message.senderAvatar}
              alt={message.senderName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          {/* Source indicator */}
          <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center shadow-sm ${sourceStyle.bg}`}>
            <i className={`${sourceIcons[message.source]} text-[8px] ${sourceStyle.text}`}></i>
          </div>
          {/* Unread dot */}
          {isUnread && (
            <span className="absolute -top-0.5 -left-0.5 w-2.5 h-2.5 rounded-full bg-primary-500 ring-2 ring-background-50"></span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Top row */}
          <div className="flex items-center justify-between gap-2">
            <span className={`text-[11px] font-semibold truncate ${isUnread ? 'text-foreground-950' : 'text-foreground-700'}`}>
              {message.senderName}
            </span>
            <span className="text-[9px] text-foreground-400 whitespace-nowrap shrink-0">
              {formatTime(message.timestamp)}
            </span>
          </div>

          {/* Type badge + source */}
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${typeStyle.bg} ${typeStyle.text}`}>
              <i className={`${typeIcons[message.type]} text-[8px]`}></i>
              {message.type === 'solicitud_cancion' ? 'Canción' : message.type === 'revision' ? 'Revisión' : message.type.charAt(0).toUpperCase() + message.type.slice(1)}
            </span>
            <span className="text-[9px] text-foreground-400 truncate">
              {message.sourceDetail}
            </span>
          </div>

          {/* Message preview */}
          <p className={`text-[10px] mt-1 line-clamp-2 leading-relaxed ${isUnread ? 'text-foreground-600 font-medium' : 'text-foreground-400'}`}>
            {message.content}
          </p>

          {/* Bottom row: status badges + song/contest info */}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {/* Priority indicator */}
            {message.priority !== 'normal' && (
              <span className={`inline-flex items-center gap-0.5 ${priorityColors[message.priority]}`}>
                <i className={`${priorityIcons[message.priority]} text-[8px]`}></i>
              </span>
            )}
            {/* Status badges */}
            {isPinned && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-violet-100 text-violet-700 text-[9px] font-semibold">
                <i className="ri-pushpin-line text-[8px]"></i>
                Fijado
              </span>
            )}
            {isFavorite && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-semibold">
                <i className="ri-star-fill text-[8px]"></i>
                Favorito
              </span>
            )}
            {message.status === 'leido_al_aire' && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[9px] font-semibold">
                <i className="ri-check-double-line text-[8px]"></i>
                Al aire
              </span>
            )}
            {message.status === 'bloqueado' && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 text-[9px] font-semibold">
                <i className="ri-forbid-2-line text-[8px]"></i>
                Bloqueado
              </span>
            )}
            {message.status === 'archivado' && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-foreground-100 text-foreground-500 text-[9px] font-medium">
                <i className="ri-archive-line text-[8px]"></i>
                Archivado
              </span>
            )}
            {message.status === 'pendiente' && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md ${statusStyle.bg} ${statusStyle.text} text-[9px] font-semibold`}>
                <i className="ri-time-line text-[8px]"></i>
                Pendiente
              </span>
            )}
            {/* Song request */}
            {message.songRequest && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[9px] font-medium">
                <i className="ri-music-line text-[8px]"></i>
                {message.songRequest}
              </span>
            )}
            {/* Contest answer */}
            {message.contestAnswer && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[9px] font-medium">
                <i className="ri-trophy-line text-[8px]"></i>
                {message.contestAnswer}
              </span>
            )}
          </div>

          {/* Quick actions on hover — admin view only */}
          {!isHostView && (
            <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => { e.stopPropagation(); onToggleFavorite(message); }}
                className={`w-6 h-6 rounded-md flex items-center justify-center cursor-pointer transition-colors ${isFavorite ? 'bg-amber-100 text-amber-600' : 'bg-background-100 text-foreground-400 hover:bg-amber-50 hover:text-amber-500'}`}
                title={isFavorite ? 'Quitar favorito' : 'Favorito'}
              >
                <i className={`${isFavorite ? 'ri-star-fill' : 'ri-star-line'} text-[11px]`}></i>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onTogglePin(message); }}
                className={`w-6 h-6 rounded-md flex items-center justify-center cursor-pointer transition-colors ${isPinned ? 'bg-violet-100 text-violet-600' : 'bg-background-100 text-foreground-400 hover:bg-violet-50 hover:text-violet-500'}`}
                title={isPinned ? 'Quitar fijado' : 'Fijar'}
              >
                <i className={`${isPinned ? 'ri-pushpin-fill' : 'ri-pushpin-line'} text-[11px]`}></i>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onMarkAir(message); }}
                className="w-6 h-6 rounded-md flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-emerald-50 hover:text-emerald-500 transition-colors"
                title="Leer al aire"
              >
                <i className="ri-broadcast-line text-[11px]"></i>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onArchive(message); }}
                className="w-6 h-6 rounded-md flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-foreground-200 hover:text-foreground-600 transition-colors"
                title="Archivar"
              >
                <i className="ri-archive-line text-[11px]"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}